import api, { TBodyResponse } from 'config/api'
import { EExchangeDirections, EUserLoanFlow, IExchangeData, IFormattedLoan } from 'store/fetchers/loans/enum'

import { ITransaction } from '../users/types'

type ELoanReceiveTo = 'external_wallet' | 'internal_wallet'

/*
estimate loan amount in form
 */

export const estimateLoanAmount = async (params: {
  from_code: string
  from_network: string
  to_code: string
  to_network: string
  amount: string
  exchange: EExchangeDirections
  ltv_percent: string
  receive_to?: ELoanReceiveTo
  expected_close_date?: string
  date_from?: string
  is_dynamic?: string
  round_direction?: 'down' | 'up'
  referral_code?: string
}): Promise<{
  result: boolean
  response:
    | {
        message: string
      }
    | IExchangeData
}> => {
  try {
    const { data } = await api.get('/v2/loans/estimate', {
      params: params,
    })
    if (!data.result) {
      throw new Error(data.message)
    }
    return data
  } catch (error) {
    let errorMessage = null

    switch (error.response?.message) {
      case 'This loan pair is not supported at the moment.':
        errorMessage = 'loan_pair_not_supported'
        break
      default:
        if (error.response?.message?.includes('min amount')) {
          errorMessage = 'min_amount_is'
        } else {
          errorMessage = error.response?.message
        }
    }
    return {
      result: false,
      response: {
        message: errorMessage,
      },
    }
  }
}

export const checkLiquidity = async (params: {
  from_code: string
  from_network: string
  to_code: string
  to_network: string
}) => {
  try {
    const { data } = await api.get('/v2/loans/check-liquidity', {
      params,
    })

    if (!data.result) {
      throw new Error(data.message)
    }

    return data?.response
  } catch (e) {
    return e?.response?.message
  }
}

/*
estimate repayment amount
 */

export const estimateRepaymentAmount = async (
  id: string,
  params: {
    receive_from: string
    receive_by_network?: string
    receive_by_code?: string
    amount?: string
  },
): Promise<{
  result: boolean
  response: {
    amount: string
    marginCall: string
    withdrawalFee?: string
  }
}> => {
  try {
    const { data } = await api.get(`/v2/loans/${id}/pledge/estimate`, {
      params: params,
    })
    if (!data.result) {
      throw new Error(data.message)
    }
    return data
  } catch (error) {
    throw new Error(error.response.message)
  }
}

/*
create loan
 */

export const createLoan = async (params: {
  user_id?: string
  loan: {
    currency_code: string
    currency_network: string
    receive_to?: ELoanReceiveTo
  }
  deposit: {
    currency_code: string
    currency_network: string
    expected_amount: string
  }
  ltv_percent: string
  expected_close_date?: string
  is_dynamic?: boolean
}): Promise<IFormattedLoan | string> => {
  try {
    const { data } = await api.post('/v2/loans', {
      params: {
        ...params,
        client: 'web',
      },
    })
    return data.response
  } catch (err) {
    const msg = err?.response?.message ?? err?.props?.response?.message ?? err?.props?.response?.error ?? ''

    if (msg?.includes('KYC')) {
      return 'KYC'
    }

    if (msg?.toLowerCase()?.includes('dynamic loan')) {
      return msg
    }

    return null
  }
}

/*
confirm create loan
 */

export const confirmCreateLoan = async ({
  loan_id,
  ...params
}: {
  user_id?: string
  loan_id: string
  loan: {
    receive_address?: string
    receive_extra_id?: string
    receive_to?: ELoanReceiveTo
    receive_from?: ELoanReceiveTo
  }
  agreed_to_tos?: boolean
  expected_close_date?: string
  is_dynamic?: boolean
}): Promise<
  | TBodyResponse<{
      incorrect_price: boolean
      not_enough_liquidity: boolean
      liquidity_delta?: number
      address?: string
      extraId?: string
    }>
  | false
  | string
> => {
  try {
    const { data } = await api.post(`/v2/loans/${loan_id}/confirm`, {
      params: params,
    })
    return data
  } catch (err) {
    const msg = err?.response?.message ?? err?.props?.response?.message ?? err?.props?.response?.error ?? ''

    if (msg?.includes('KYC')) {
      return msg
    }

    if (msg?.toLowerCase()?.includes('dynamic loan')) {
      return msg
    }

    return false
  }
}

/*
fetch single loan by id
 */

export const fetchUserLoans = async (): Promise<IFormattedLoan[]> => {
  try {
    const { data } = await api.get('/v2/loans')
    return data?.response || []
  } catch (e) {
    return []
  }
}

/*
fetch single loan by id
 */

export const _fetchLoanById = async ({ loan_id }: { loan_id: string }): Promise<IFormattedLoan> => {
  try {
    const { data } = await api.get(`/v2/loans/${loan_id}`)
    return data?.response
  } catch (ignore) {
    return null
  }
}

/*
verification create new loan by code from twilio
 */

export interface IIncreaseDepositAmountResult {
  result: boolean
  response: {
    address: string
    extraId: string
    isFallbackTx: boolean
    real_increase_amount: number
    transactionId?: string
  }
  message?: string
  code?: string
}

export const increaseDepositAmount = async ({
  amount,
  loan_id,
  receive_from,
}: {
  loan_id: string
  amount: string
  receive_from?: string
}): Promise<IIncreaseDepositAmountResult> => {
  try {
    const { data } = await api.post(`/v2/loans/${loan_id}/increase`, {
      params: {
        deposit: {
          amount,
          receive_from,
        },
      },
    })

    return {
      result: data.result,
      response: data.response,
      message: data.message,
    }
  } catch (err) {
    return {
      result: false,
      message: err?.response?.message ?? err?.props?.response?.message ?? err?.props?.response?.error,
      response: null,
    }
  }
}

/*
verification create new loan by code from twilio
 */

export const createPledgeRedemption = async ({
  loan_id,
  ...params
}: {
  loan_id: string
  address: string
  extra_id: string
  verification_token?: string
  code_2fa: string
  receive_from?: EUserLoanFlow
  amount?: string
  repay_by_code?: string
  repay_by_network?: string
  add_to_whitelist?: boolean
  address_name?: string
  receive_to?: string
}) => {
  try {
    const { data } = await api.post(`/v2/loans/${loan_id}/pledge`, {
      params: params,
    })

    return data?.result ? data?.response : false
  } catch (err) {
    return err?.response?.message || ''
  }
}

export const updateInitialDeposit = async ({ loan_id }: { loan_id: string }): Promise<boolean> => {
  try {
    const { data } = await api.post(`/v2/loans/${loan_id}/deposit`)

    return data.result
  } catch (ignore) {
    return false
  }
}

/*
estimate increase amount for loan by id
 */

export const estimateIncreaseAmount = async ({
  loan_id,
  amount,
  receive_from,
}: {
  loan_id: string
  amount: string
  receive_from?: EUserLoanFlow
}): Promise<
  | {
      is_error: undefined
      deposit: {
        amount: string
        new_amount: string
      }
      max: string
      liquidation_price: string
      precision: number
      real_increase_amount: number
    }
  | { is_error: true; error: Error }
> => {
  try {
    const { data } = await api.get(`/v2/loans/${loan_id}/increase/estimate`, {
      params: {
        amount: amount,
        receive_from: receive_from,
      },
    })

    return data.response
  } catch (ignore) {
    return null
  }
}

export const updateIncreaseTxHash = async ({
  loan_id,
  ...body
}: {
  loan_id: string
  hash: string
}): Promise<{
  result: boolean
  error?: string
}> => {
  try {
    const { data } = await api.put(`/v2/loans/${loan_id}/increase/fallback-tx`, {
      params: body,
    })

    return {
      result: data.result,
    }
  } catch (error) {
    const errorMessage = error.response?.data?.response?.message || 'Request error'

    return {
      result: false,
      error: errorMessage,
    }
  }
}

export const getTotalAmountOfLoans = async () => {
  try {
    const { data } = await api.get(`/v2/loans/summary`)
    return data.response
  } catch (e) {
    return false
  }
}

export const getTotalAmountOfBalance = async () => {
  try {
    const { data } = await api.get(`/v2/wallet/balance/summary`)

    return data.response
  } catch (e) {
    return false
  }
}

export const getAllTransactions = async (): Promise<ITransaction[]> => {
  try {
    const { data } = await api.get(`/v2/loans/transactions`)
    return data.response
  } catch (ignore) {
    return null
  }
}

export const getAmountToIncrease = async (loanId: string): Promise<string> => {
  try {
    const { data } = await api.get(`/v2/loans/${loanId}/get-amount-to-increase`)
    return data.response
  } catch (ignore) {
    return null
  }
}

export const autoIncrease = async (params: {
  auto_increase: boolean
  code_2fa: string
  verification_token: string
  loan_id: string
}): Promise<string> => {
  try {
    const { data } = await api.post(`/v2/loans/${params.loan_id}/increase/auto`, { params })
    return data.message
  } catch (e) {
    return e?.response?.message || e?.response?.error || ''
  }
}
